import {
  createEffect,
  createSignal,
  createUniqueId,
  For,
  on,
  onCleanup,
  onMount,
  Show,
  untrack,
  type JSX,
} from "solid-js";

import { Button } from "~/common/button";
import clsx from "~/util/html/style";

import { ArtDefs, ArtIdProvider } from "./art/art_defs";
import styles from "./deck.module.css";
import type { Markup } from "./markup";
import {
  createPen,
  MarkupLayer,
  type MarkupLabels,
  MarkupStrokes,
  MarkupToolbar,
} from "./markup_layer";
import { Slide } from "./slide";
import { STAGE_HEIGHT, STAGE_WIDTH } from "./stage";
import type { Deck, PictureSrc } from "./types";

const THUMB_WIDTH = 152;

/** Where a key moves the deck from `index`, or null for a key the deck ignores. */
function keyTarget(key: string, index: number, last: number): number | null {
  switch (key) {
    case "ArrowRight":
    case "PageDown":
    case " ":
      return index + 1;
    case "ArrowLeft":
    case "PageUp":
      return index - 1;
    case "Home":
      return 0;
    case "End":
      return last;
    default:
      return null;
  }
}

// A key typed into a field is the field's, Space on a button already clicks it, and Space on a playing video pauses it.
// The arrows always move the deck, even from a video, so a presenter's clicker never seeks one instead.
function ownsKey(target: EventTarget | null, key: string): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.matches("input, textarea, select")) return true;
  return key === " " && target.matches("button, a[href], summary, [role='button'], video");
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function DeckViewer(props: {
  deck: Deck;
  pictureSrc: PictureSrc;
  /** What a picture that fails to load says in its place. Default "Couldn't load this picture": only the live staff
   *  page has a reason to say "This picture changed". */
  failedPictureText?: string;
  /** "fixed": a 1280x720 design stage scaled into the box, so container queries always see 1280px (staff
   *  preview, presenter, snapshot page, thumbnails). "responsive": the stage is the box's own width, so a phone
   *  gets the single-column reflow (patient page). */
  layout: "fixed" | "responsive";
  /** Fixed layout only: "width" scales to the box's width, "contain" fits both width and height. Default "width". */
  fit?: "width" | "contain";
  /** Controlled slide index; omit and the viewer owns it. */
  index?: number;
  onIndexChange?: (index: number) => void;
  /** Back, "3 / 8", Next under the stage. Default true. */
  controls?: boolean;
  /** Thumbnails of every slide under the controls. Default false. */
  filmstrip?: boolean;
  /** "stage": arrow keys while the stage has focus (tabindex 0). "document": anywhere (presenter, patient page). Default "stage". */
  keyboard?: "stage" | "document";
  /** Dark chrome around the stage (presenter). Default false. */
  dark?: boolean;
  /** The controls' words in the reader's language (patient page). Default English. */
  labels?: { slides: string; back: string; next: string };
  /** Fixed layout only: what is drawn on each slide, kept by slide key, with Draw beside the controls (presenting
   *  screens). Omit and the slides take no marks. */
  markup?: Markup;
  /** With markup: false shows its lines without Draw, as a record does. Default true. */
  drawing?: boolean;
  /** The drawing tools' words in the reader's language (the office tablet). Default English. */
  drawLabels?: MarkupLabels;
  class?: string;
}): JSX.Element {
  // Each viewer draws with its own gradient, mask and filter ids, so two decks on one page never share them.
  const artPrefix = createUniqueId();
  const [ownIndex, setOwnIndex] = createSignal(0);
  const total = () => props.deck.slides.length;
  const last = () => Math.max(total() - 1, 0);
  const clamp = (value: number) => Math.min(Math.max(value, 0), last());
  const requested = () => props.index ?? ownIndex();
  const index = () => clamp(requested());
  const keyboard = () => props.keyboard ?? "stage";
  const fit = () => props.fit ?? "width";
  const currentKey = () => props.deck.slides[index()]?.key;
  const pen = createPen();
  const drawable = () => props.drawing ?? true;
  const markup = () => (props.layout === "fixed" ? props.markup : undefined);
  // Draw sits beside the controls, so a viewer without them shows the lines alone.
  const tools = () => (drawable() && (props.controls ?? true) ? markup() : undefined);

  const go = (value: number) => {
    const next = clamp(value);
    if (next === index()) return;
    if (props.index === undefined) setOwnIndex(next);
    props.onIndexChange?.(next);
  };

  // A deck that shrinks under the index pulls it back to the new last slide.
  createEffect(() => {
    const wanted = requested();
    const clamped = clamp(wanted);
    if (clamped === wanted) return;
    untrack(() => {
      if (props.index === undefined) setOwnIndex(clamped);
      props.onIndexChange?.(clamped);
    });
  });

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.isComposing || (event.key === " " && event.shiftKey)) return;
    const target = keyTarget(event.key, index(), last());
    if (target === null || ownsKey(event.target, event.key)) return;
    event.preventDefault();
    go(target);
  };

  createEffect(() => {
    if (keyboard() !== "document") return;
    document.addEventListener("keydown", onKeyDown);
    onCleanup(() => document.removeEventListener("keydown", onKeyDown));
  });

  let box!: HTMLDivElement;
  const [boxSize, setBoxSize] = createSignal(
    { width: 0, height: 0 },
    { equals: (a, b) => a.width === b.width && a.height === b.height },
  );
  onMount(() => {
    const measure = () => setBoxSize({ width: box.clientWidth, height: box.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    onCleanup(() => observer.disconnect());
  });

  const scale = () => {
    const size = boxSize();
    const byWidth = size.width / STAGE_WIDTH;
    return fit() === "contain" ? Math.min(byWidth, size.height / STAGE_HEIGHT) : byWidth;
  };

  const fixedStage = (): JSX.CSSProperties =>
    fit() === "contain"
      ? {
          width: `${STAGE_WIDTH}px`,
          height: `${STAGE_HEIGHT}px`,
          left: "50%",
          top: "50%",
          transform: `translate(-50%, -50%) scale(${scale()})`,
        }
      : {
          width: `${STAGE_WIDTH}px`,
          height: `${STAGE_HEIGHT}px`,
          left: "0",
          top: "0",
          transform: `scale(${scale()})`,
          "transform-origin": "0 0",
        };

  // A reflowed slide can be taller than a phone's screen, and the controls sit under it: after a move, bring the new
  // slide's top back into view instead of leaving the reader at its bottom.
  createEffect(
    on(
      index,
      () => {
        if (props.layout !== "responsive" || box.getBoundingClientRect().top >= 0) return;
        box.scrollIntoView({
          block: "start",
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
      },
      { defer: true },
    ),
  );

  let film: HTMLDivElement | undefined;
  // Keep the current thumbnail in view as the deck moves.
  createEffect(() => {
    const thumb = film?.children[index()];
    if (!film || !(thumb instanceof HTMLElement)) return;
    film.scrollTo({
      left: Math.max(0, thumb.offsetLeft - film.clientWidth / 2 + thumb.clientWidth / 2),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  });

  // Only the current slide is mounted on the stage.
  const CurrentSlide = () => (
    <Show when={props.deck.slides[index()]}>
      {(slide) => (
        <Slide
          slide={slide()}
          deck={props.deck}
          number={index() + 1}
          total={total()}
          pictureSrc={props.pictureSrc}
          failedPictureText={props.failedPictureText}
        />
      )}
    </Show>
  );

  return (
    <div
      class={clsx(
        "relative flex flex-col gap-3",
        props.layout === "fixed" && fit() === "contain" && "h-full min-h-0",
        props.class,
      )}
    >
      <ArtDefs prefix={artPrefix} />
      <ArtIdProvider prefix={artPrefix}>
        <div
          ref={box}
          class={clsx(
            "relative w-full overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2",
            props.layout === "responsive" && styles["stage"],
            props.layout === "responsive" && "rounded-xl",
            props.layout === "fixed" && fit() === "width" && "aspect-video rounded-xl",
            props.layout === "fixed" && fit() === "contain" && "min-h-0 flex-1",
            // A drag that draws must not also select the slide's words.
            pen.on() && "select-none",
          )}
          role="region"
          aria-roledescription="carousel"
          aria-label={props.labels?.slides ?? "Slides"}
          tabIndex={keyboard() === "stage" ? 0 : undefined}
          onKeyDown={(event) => {
            if (keyboard() === "stage") onKeyDown(event);
          }}
        >
          <Show when={props.layout === "fixed"} fallback={<CurrentSlide />}>
            <div class={clsx(styles["stage"], "absolute")} style={fixedStage()}>
              <CurrentSlide />
              <Show when={markup()}>
                {(sheet) => (
                  <Show when={currentKey()}>
                    {(key) => <MarkupLayer markup={sheet()} slide={key()} pen={pen} />}
                  </Show>
                )}
              </Show>
            </div>
          </Show>
        </div>

        <Show when={tools()}>
          {(sheet) => (
            <Show when={pen.on()}>
              <MarkupToolbar
                markup={sheet()}
                slide={currentKey()}
                pen={pen}
                dark={props.dark}
                labels={props.drawLabels}
              />
            </Show>
          )}
        </Show>

        <Show when={props.controls ?? true}>
          {/* Back, the counter and Next stay centered whether or not Draw sits at the end of the row. */}
          <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <span />
            <div class="flex items-center justify-center gap-3">
              <Button
                variant={props.dark ? "plain" : "secondary"}
                dark={props.dark}
                size="small"
                leftIconName="arrow-left"
                disabled={index() === 0}
                onClick={() => go(index() - 1)}
              >
                {props.labels?.back ?? "Back"}
              </Button>
              <span
                aria-live="polite"
                class={clsx(
                  "min-w-14 text-center text-sm font-semibold tabular-nums",
                  props.dark ? "text-white/70" : "text-typography-secondary",
                )}
              >
                {`${index() + 1} / ${total()}`}
              </span>
              <Button
                variant={props.dark ? "plain" : "secondary"}
                dark={props.dark}
                size="small"
                rightIconName="arrow-right"
                disabled={index() === last()}
                onClick={() => go(index() + 1)}
              >
                {props.labels?.next ?? "Next"}
              </Button>
            </div>
            <div class="flex justify-end">
              <Show when={tools()}>
                {(sheet) => (
                  <Show when={!pen.on()}>
                    <MarkupToolbar
                      markup={sheet()}
                      slide={currentKey()}
                      pen={pen}
                      dark={props.dark}
                      labels={props.drawLabels}
                    />
                  </Show>
                )}
              </Show>
            </div>
          </div>
        </Show>

        <Show when={props.filmstrip}>
          <div ref={film} class="relative flex gap-2.5 overflow-x-auto px-0.5 pt-0.5 pb-2.5">
            <For each={props.deck.slides}>
              {(slide, position) => (
                <button
                  type="button"
                  class={clsx(
                    "relative aspect-video shrink-0 overflow-hidden rounded-md transition-opacity",
                    position() === index()
                      ? clsx("ring-2", props.dark ? "ring-white" : "ring-brand-primary")
                      : "opacity-75 ring-1 ring-brand-subtle hover:opacity-100",
                  )}
                  style={{ width: `${THUMB_WIDTH}px` }}
                  aria-label={`Show slide ${position() + 1}`}
                  aria-current={position() === index() ? "true" : undefined}
                  onClick={() => go(position())}
                >
                  <div
                    class={clsx(styles["stage"], "pointer-events-none absolute top-0 left-0")}
                    style={{
                      width: `${STAGE_WIDTH}px`,
                      transform: `scale(${THUMB_WIDTH / STAGE_WIDTH})`,
                      "transform-origin": "0 0",
                    }}
                    aria-hidden="true"
                  >
                    <Slide
                      slide={slide}
                      deck={props.deck}
                      number={position() + 1}
                      total={total()}
                      pictureSrc={props.pictureSrc}
                      failedPictureText={props.failedPictureText}
                    />
                    <Show when={markup()}>
                      {(sheet) => (
                        <svg
                          class="absolute inset-0 size-full"
                          viewBox={`0 0 ${STAGE_WIDTH} ${STAGE_HEIGHT}`}
                        >
                          <MarkupStrokes strokes={sheet().strokes(slide.key)} />
                        </svg>
                      )}
                    </Show>
                  </div>
                </button>
              )}
            </For>
          </div>
        </Show>
      </ArtIdProvider>
    </div>
  );
}
